// anime.js choreography layer — loads animejs v4 from esm.sh lazily, and only
// when rAF is healthy (window.__rafOk === true, i.e. the real deployed site).
// In the throttled editor preview nothing here runs, so the page renders
// static-but-complete (same contract as framer-motion's motionOk gating).
//
// Concepts from the animejs.com "composition / tween parameters" docs:
//   - a chained Timeline with position offsets (sequential choreography)
//   - stagger() for multi-target cascades
//   - alternating infinite loops for ambient motion
//   - additive 'blend' composition (two simultaneous tweens on the SAME
//     property per target are blended, so hovers/entrances never fight the float)
//   - parameterized spring/back easings + value-array keyframes
//
// Debug: window.__af holds live handles (tl, ambient tweens, ring) so a
// mid-state can be inspected via .seek() in page_eval.
import { motionOk } from "./motionOk.js";

let started = false;

function reduceMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export async function runAllFX() {
  if (!motionOk || started) return;
  if (reduceMotion()) {
    started = true;
    return;
  }
  started = true;

  const { animate, createTimeline, stagger } = await import("animejs");

  const H = (window.__af = {});

  // ---------- HERO INTRO — chained timeline with position offsets ----------
  let ambientStarted = false;
  const startAmbient = () => {
    if (ambientStarted) return;
    ambientStarted = true;
    // additive 'blend': two simultaneous tweens on the same translateY per
    // target blend into an organic float (a coarse bob + a fine micro-jitter).
    H.devFloat1 = animate(".af-float", { translateY: [-9, 0], duration: 4600, ease: "inOutSine", loop: true, alternate: true });
    H.devFloat2 = animate(".af-float", { translateY: [-2.5, 2.5], duration: 1400, ease: "inOutSine", loop: true, alternate: true, composition: "blend" });
    H.devFloat3 = animate(".af-float2", { translateY: [-7, 0], duration: 5400, ease: "inOutSine", loop: true, alternate: true });
    H.devFloat4 = animate(".af-float2", { translateY: [-2, 2], duration: 1600, ease: "inOutSine", loop: true, alternate: true, composition: "blend" });
    H.chipFloat1 = animate(".af-chip", { translateY: [-7, 0], duration: 3800, ease: "inOutSine", loop: true, alternate: true });
    H.chipFloat2 = animate(".af-chip", { translateY: [-1.8, 1.8], duration: 1300, ease: "inOutSine", loop: true, alternate: true, composition: "blend" });
  };

  const tl = createTimeline({
    defaults: { ease: "outExpo" },
    onComplete: startAmbient,
  });

  // set() pins the from-state instantly so there is no flash before each tween
  tl.set(".af-hero-badge", { opacity: 0, translateY: -14 });
  tl.set(".af-word", { opacity: 0, translateY: 18 });
  tl.set(".af-highlight", { opacity: 0, scaleX: 0 });
  tl.set(".af-hero-sub", { opacity: 0, translateY: 20 });
  tl.set(".af-hero-ctas", { opacity: 0, scale: 0.92 });
  tl.set(".af-hero-rate", { opacity: 0, translateX: -18 });
  tl.set(".af-hero-stat", { opacity: 0, translateY: 16 });
  tl.set(".af-devicewrap", { opacity: 0, translateY: 46 });
  tl.set(".af-chip", { opacity: 0, scale: 0.6 });
  tl.set(".af-ring", { opacity: 0 });

  tl.add(".af-hero-badge", { opacity: 1, translateY: 0, duration: 500 }, 100);
  tl.add(".af-word", { opacity: 1, translateY: 0, duration: 600, delay: stagger(24) }, "+=140");
  tl.add(".af-highlight", { opacity: 1, scaleX: 1, duration: 480, ease: "outBack(1.7)" }, "+=60");
  tl.add(".af-hero-sub", { opacity: 1, translateY: 0, duration: 620 }, "+=80");
  tl.add(".af-hero-ctas", { opacity: 1, scale: 1, duration: 560, ease: "outBack(1.6)" }, "+=60");
  tl.add(".af-hero-rate", { opacity: 1, translateX: 0, duration: 560 }, "+=70");
  tl.add(".af-hero-stat", { opacity: 1, translateY: 0, duration: 520, delay: stagger(90) }, "+=70");
  tl.add(".af-float", { opacity: 1, translateY: 0, duration: 950, ease: "outElastic(1, 0.6)" }, "+=80");
  tl.add(".af-float2", { opacity: 1, translateY: 0, duration: 1000, ease: "outElastic(1, 0.6)" }, "+=90");
  tl.add(".af-chip", { opacity: 1, scale: 1, duration: 520, ease: "outBack(2)", delay: stagger(90) }, "+=120");
  tl.add(".af-ring", { opacity: 1, duration: 700 }, 400);

  H.heroTL = tl;

  // rotating ring — independent of the intro chain, runs from load
  H.ring = animate(".af-ring-inner", { rotate: 360, duration: 26000, ease: "linear", loop: true });

  // ---------- ROADMAP LINE DRAW — scroll-triggered (IntersectionObserver) ----------
  const roadLine = document.querySelector(".af-roadmap-line");
  const roadSec = document.getElementById("proses");
  if (roadLine && roadSec && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            io.disconnect();
            const len = roadLine.getTotalLength();
            roadLine.style.strokeDasharray = String(len);
            roadLine.style.strokeDashoffset = String(len);
            H.roadDraw = animate(roadLine, { strokeDashoffset: [len, 0], duration: 1500, ease: "outExpo" });
          }
        }
      },
      { threshold: 0.3 }
    );
    io.observe(roadSec);
  }
}
